import { positionPopover, unfoldPopover, slideIn, growWindow, shrinkWindow } from './popover.js?v=20261104-motion';

const formStates = new WeakMap();
// 2026-11-04 (Owner): a changed amount or name rolls into place (base.css data-roll); the text is already the new one.
const roll = (node) => {
  if (node) node.dataset.roll = node.dataset.roll === 'a' ? 'b' : 'a';
};
const exclusionStates = new WeakMap();
// Keep initial defaults in an unopened branch, but never restore a customer's
// cleared answer on reopening or on an editor move of the same input node.
const activatedControls = new WeakSet();

export function accessoryItem({ id, quantity, minimum = 1, maximum, increment = 1, available }, properties) {
  const count = Number(quantity);
  const min = Number(minimum || 1);
  const step = Number(increment || 1);
  const max = maximum ? Number(maximum) : Infinity;
  if (
    !available ||
    !/^\d+$/.test(String(id)) ||
    !Number.isSafeInteger(Number(id)) ||
    Number(id) < 1 ||
    !Number.isSafeInteger(count) ||
    count < min ||
    count > max ||
    step < 1 ||
    (count - min) % step !== 0
  )
    return null;
  return { id: String(id), quantity: count, properties: { ...properties } };
}

export function propertyLabel(parts) {
  return parts
    .map((value) => String(value || '').trim())
    .filter(Boolean)
    .join(' / ');
}

// Shopify reads "[...]" in a form property name as nested keys, so the shared cart
// rejects such keys. A merchant heading like "[Optional] Wrap" must still be a valid
// line property name; only the brackets are swapped, the visible wording stays.
export function propertyKey(title) {
  return String(title || '')
    .trim()
    .replaceAll('[', '(')
    .replaceAll(']', ')');
}

// Scrolling or resizing closes an open popover. Listen only while one is open
// so ordinary page scrolling runs no options code at all. beforetoggle fires
// synchronously on open, so even an immediate scroll is caught.
function closeOnScrollWhileOpen(popover, close, signal, viewport = false) {
  let watch = null;
  signal.addEventListener('abort', () => watch?.abort(), { once: true });
  popover.addEventListener(
    'beforetoggle',
    (event) => {
      watch?.abort();
      watch = null;
      if (event.newState !== 'open' || signal.aborted) return;
      watch = new AbortController();
      const options = { signal: watch.signal, passive: true };
      document.addEventListener(
        'scroll',
        (scroll) => {
          if (!popover.contains(scroll.target)) close();
        },
        { ...options, capture: true },
      );
      window.addEventListener('resize', close, options);
      if (viewport) window.visualViewport?.addEventListener('resize', close, options);
    },
    { signal },
  );
}

// 3.0.1 (Owner): focus leaving a window closes it, but focus moving to an element around the window does not leave
// it. WebKit never focuses a pressed button, so a finger on a row hands focus to the page's main; closing there hid
// the window before the finger lifted, and Safari, which cannot keep a closing window on top, let the content after
// it take the tap: on an iPhone a window that opened downward never chose its row.
function focusLeaves(panel, next, owner) {
  return Boolean(next) && !panel.contains(next) && !next.contains(panel) && next !== owner;
}

// 3.0.0 (Owner): brings a blocked group into view just below the sticky header, gliding unless motion is reduced, and
// resolves once the page has stopped. A group already in full view stays where it is.
function glide(target, reduced, signal) {
  return new Promise((resolve) => {
    if (signal.aborted || !target.isConnected) {
      resolve();
      return;
    }
    const host = document.querySelector('.header-motion-host');
    const offset = (host && getComputedStyle(host).position === 'sticky' ? host.offsetHeight : 0) + 12;
    const rect = target.getBoundingClientRect();
    if (rect.top >= offset && rect.bottom <= innerHeight) {
      resolve();
      return;
    }
    const end = Math.max(0, Math.min(scrollY + rect.top - offset, document.documentElement.scrollHeight - innerHeight));
    scrollTo({ top: end, behavior: reduced ? 'instant' : 'smooth' });
    if (reduced) {
      resolve();
      return;
    }
    const start = performance.now();
    const stop = new AbortController();
    let frame;
    const done = () => {
      stop.abort();
      cancelAnimationFrame(frame);
      signal.removeEventListener('abort', cancel);
      resolve();
    };
    const cancel = () => {
      scrollTo({ top: scrollY, behavior: 'instant' });
      done();
    };
    signal.addEventListener('abort', cancel, { once: true });
    addEventListener('scrollend', done, { signal: stop.signal, once: true });
    const watch = () => {
      if (stop.signal.aborted) return;
      if (!target.isConnected) cancel();
      else if (Math.abs(scrollY - end) < 1 || performance.now() - start > 1200) done();
      else frame = requestAnimationFrame(watch);
    };
    frame = requestAnimationFrame(watch);
  });
}

// Totals are recalculated on every selection and quantity input; building an
// Intl.NumberFormat each time was the costliest part of that sync.
const currencyFormatters = new Map();
function currencyFormat(currency, amount) {
  const locale = document.documentElement.lang || 'en';
  const key = locale + '|' + currency;
  if (!currencyFormatters.has(key))
    currencyFormatters.set(key, new Intl.NumberFormat(locale, { style: 'currency', currency }));
  return currencyFormatters.get(key).format(amount / 100);
}

function hiddenByBranch(node) {
  for (let parent = node.parentElement; parent; parent = parent.parentElement) {
    if (parent.matches('[data-options-branch]') && parent.hidden) return true;
  }
  return false;
}

function clearControl(control) {
  if (control.matches('[type=radio], [type=checkbox]')) control.checked = false;
  else if (control.matches('[data-options-addon-quantity]')) control.value = '1';
  else control.value = '';
  control.required = false;
  control.setCustomValidity?.('');
}

export class OptionsController {
  constructor(root, form) {
    this.root = root;
    this.optionSet = root.closest('[data-options-set]');
    exclusionStates.delete(this.optionSet || root);
    // Repeated instances can reuse Liquid block IDs. Keep labels and popovers
    // attached to their own inputs even when the exact markup is cloned.
    const duplicateIds = new Map();
    for (const node of root.querySelectorAll('[id]')) {
      if (document.getElementById(node.id) === node) continue;
      const previous = node.id;
      node.id = previous + '-' + crypto.randomUUID();
      duplicateIds.set(previous, node.id);
    }
    if (duplicateIds.size)
      for (const node of root.querySelectorAll(
        '[for], [aria-describedby], [aria-labelledby], [aria-controls], [popovertarget]',
      ))
        for (const attribute of ['for', 'aria-describedby', 'aria-labelledby', 'aria-controls', 'popovertarget']) {
          const value = node.getAttribute(attribute);
          if (value)
            node.setAttribute(
              attribute,
              value
                .split(' ')
                .map((id) => duplicateIds.get(id) || id)
                .join(' '),
            );
        }
    this.form = form;
    this.abort = new AbortController();
    this.message = root.querySelector('[data-options-message]');
    // A Buy block has its own unique form ID. Associate every external control
    // before enabling it, irrespective of the two modules' execution order.
    for (const control of root.querySelectorAll('input, select, textarea, button'))
      control.setAttribute('form', form.getAttribute('id'));
    const signal = this.abort.signal;
    let state = formStates.get(form);
    if (!state) {
      state = { groups: new Set(), identity: crypto.randomUUID() };
      formStates.set(form, state);
    }
    this.state = state;
    state.groups.add(this);
    for (const choice of this.choices()) {
      const triggers = this.triggers(choice);
      const name = `options-choice-${crypto.randomUUID()}`;
      const selected = triggers.find((trigger) => trigger.checked);
      const defaultSelected = triggers.find((trigger) => trigger.defaultChecked);
      for (const trigger of triggers) {
        trigger.type = choice.dataset.optionsMode === 'single' ? 'radio' : 'checkbox';
        if (choice.dataset.optionsPresentation === 'multiple') trigger.setAttribute('role', 'switch');
        else trigger.removeAttribute('role');
        if (trigger.type === 'radio') trigger.name = name;
      }
      if (choice.dataset.optionsMode === 'single') {
        for (const trigger of triggers) trigger.defaultChecked = trigger === defaultSelected;
        for (const trigger of triggers) trigger.checked = trigger === selected;
      }
    }
    root.addEventListener(
      'input',
      (event) => {
        this.syncAll(event.target);
      },
      { signal },
    );
    root.addEventListener(
      'invalid',
      (event) => {
        // Native form validation runs before submit/prepare. A clipped image
        // radio cannot reliably display the browser's validation bubble.
        event.preventDefault();
        const control = event.target;
        const label = control.closest('[data-options-choice]')?.dataset.optionsLabel || root.dataset.optionsTitle;
        if (this.message) this.message.textContent = `${label}: ${control.validationMessage}`;
        this.reveal(control);
        this.flagProblems();
      },
      { signal, capture: true },
    );
    root.addEventListener('shopify:block:select', (event) => this.reveal(event.target, false), { signal });
    const closeReasons = () => {
      for (const note of root.querySelectorAll('.options-reason-popover:popover-open')) note.hidePopover();
    };
    for (const note of root.querySelectorAll('.options-reason-popover'))
      closeOnScrollWhileOpen(note, () => note.matches(':popover-open') && note.hidePopover(), signal);
    root.addEventListener(
      'toggle',
      (event) => {
        const disclosure = event.target;
        if (disclosure.matches('[data-options-disclosure]') && !disclosure.open) {
          closeReasons();
          this.closeHelp(true);
          this.closeName?.(true);
        }
        // A folded group has no width to share; its image choices and prices are laid out when it opens.
        if (disclosure.matches('[data-options-disclosure]') && disclosure.open) {
          this.layoutImages();
          this.fitPrices();
          // 2026-11-04 (Owner): an opened group's rows slide in one after another (closing fades them, base CSS).
          if (this.motionReady) slideIn(this.rows(disclosure));
        }
        if (
          disclosure.matches('[data-options-disclosure]') &&
          !disclosure.open &&
          disclosure.contains(document.activeElement) &&
          document.activeElement !== disclosure.querySelector('summary')
        )
          disclosure.querySelector('summary')?.focus();
      },
      { signal, capture: true },
    );
    for (const image of root.querySelectorAll('.options-item-image')) {
      const failed = () => {
        image.hidden = true;
        const value = image.closest('[data-options-value]');
        value?.setAttribute('data-options-image-failed', '');
        const message = value?.querySelector('[data-options-image-error]');
        if (message) message.hidden = false;
      };
      image.addEventListener('error', failed, { signal });
      if (image.complete && !image.naturalWidth) failed();
    }
    root.addEventListener(
      'change',
      (event) => {
        if (event.target.matches('[data-options-addon-variant]')) {
          const addon = event.target.closest('[data-options-addon]');
          const minimum = event.target.selectedOptions[0]?.dataset.min || '1';
          addon.querySelector('[data-options-addon-quantity]').value = minimum;
        }
        this.syncAll();
      },
      { signal },
    );
    root.addEventListener(
      'keydown',
      (event) => {
        const trigger = event.target.closest('[data-options-trigger]');
        if (event.code === 'Space' && trigger?.type === 'radio' && trigger.checked && !trigger.disabled) {
          event.preventDefault();
          if (!event.repeat) trigger.click();
        }
      },
      { signal },
    );
    root.addEventListener(
      'click',
      (event) => {
        // 2.9.9 (Owner): an add-on's question mark only opens its description (help-window.js); it neither selects
        // the add-on nor reopens its picker.
        if (event.target.closest('[data-help-mark]')) return;
        const card = event.target.closest('.options-swatch');
        const control = card?.querySelector('[data-options-trigger]');
        if (control) {
          if (control.disabled) return;
          const selected = this.selectedTriggers?.has(control);
          const image = card.closest('[data-options-presentation]')?.dataset.optionsPresentation === 'image';
          const picker = this.variantPopovers?.find((item) => item.control === control);
          if (event.target !== control) {
            // Let the label activate and focus its native input. An ordinary
            // selected row only reopens its picker; its switch cancels it.
            if (selected && !image) {
              event.preventDefault();
              picker?.open?.();
            }
            return;
          }
          if (control.type === 'radio' && selected) {
            control.checked = false;
            control.dispatchEvent(new Event('input', { bubbles: true }));
            control.dispatchEvent(new Event('change', { bubbles: true }));
          } else this.syncAll();
          if (control.checked && !control.disabled) picker?.open?.();
          else picker?.close?.();
          return;
        }
        const reason = event.target.closest('[data-options-reason-button]');
        if (reason) {
          const note = document.getElementById(reason.getAttribute('popovertarget'));
          if (note) {
            event.preventDefault();
            if (note.matches(':popover-open')) note.hidePopover();
            else {
              note.showPopover();
              const rootSize = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
              positionPopover(note, reason, 20 * rootSize);
              // 2026-11-04 (Owner): the reason window stands up out of its "!" like the description windows.
              growWindow(note, note.dataset.side);
            }
          }
          return;
        }
        const reasonClose = event.target.closest('[data-options-reason-close]');
        if (reasonClose) {
          const note = reasonClose.closest('[data-options-conflict-reason]');
          note?.hidePopover();
          reasonClose.closest('[data-options-value]')?.querySelector('[data-options-reason-button]')?.focus();
          return;
        }
        const clear = event.target.closest('[data-options-clear]');
        if (clear) {
          this.triggers(clear.closest('[data-options-choice]')).forEach((control) => {
            control.checked = false;
          });
          this.syncAll();
        }
      },
      { signal },
    );
    form.addEventListener('meri:product:prepare', (event) => this.prepare(event), { signal });
    // 3.0.0 (Owner): Buy it now's cover, shown while the add-ons are incomplete, runs the add-to-cart checks without
    // adding anything, so a press shows the same frames, glide and shake. The first group of the form answers it.
    form.addEventListener(
      'click',
      (event) => {
        if (!event.target.closest?.('[data-accelerated-cover]') || this.state.groups.values().next().value !== this)
          return;
        event.preventDefault();
        if (!this.form.reportValidity()) return;
        const detail = { form: this.form, additionalItems: [], errors: [] };
        this.form.dispatchEvent(new CustomEvent('meri:product:prepare', { detail, cancelable: true }));
      },
      { signal },
    );
    form.addEventListener(
      'meri:variant:change',
      (event) => {
        this.variantAvailable = event.detail.available;
        this.syncAll();
      },
      { signal },
    );
    form.addEventListener('input', () => this.syncAll(), { signal });
    form.addEventListener('reset', () => queueMicrotask(() => this.syncAll()), { signal });
    window.addEventListener(
      'pageshow',
      () => {
        if ([...this.state.groups].find((group) => group.optionSet === this.optionSet) === this) this.syncAll();
      },
      { signal },
    );
    form.addEventListener(
      'meri:product:added',
      (event) => {
        if (event.detail?.form === this.form && this.state.groups.values().next().value === this) {
          this.state.identity = crypto.randomUUID();
          const identity = this.form.querySelector('[data-options-configuration]');
          if (identity) identity.value = this.state.identity;
          this.syncAll();
        }
      },
      { signal },
    );
    this.setupNames(signal);
    this.setupLift(signal);
    this.layoutImages();
    let layoutFrame = 0;
    signal.addEventListener('abort', () => cancelAnimationFrame(layoutFrame), { once: true });
    window.addEventListener(
      'resize',
      () => {
        cancelAnimationFrame(layoutFrame);
        layoutFrame = requestAnimationFrame(() => {
          this.layoutImages();
          this.fitPrices();
        });
      },
      { signal, passive: true },
    );
    // Web fonts change the width of a price; it is fitted again once they have loaded.
    document.fonts?.ready.then(() => !signal.aborted && this.fitPrices());
    this.setupDropdowns();
    this.setupVariantPopovers();
    this.syncAll();
    this.fitPrices();
    root.removeAttribute('data-options-pending');
    // 2026-11-04 (Owner): a group slides in as it appears, after the groups above it on the same product. The theme
    // editor redraws a block on every change, so there it just appears. Only later changes move; this first sync does
    // not.
    if (root.dataset.optionsEditor !== 'true')
      slideIn([root.querySelector('.options-group-frame'), root.querySelector('[data-options-all-cost]')], {
        from: 0.3,
        stagger: 45,
        duration: 640,
        delay: Math.max(0, [...state.groups].indexOf(this)) * 45,
      });
    requestAnimationFrame(() => {
      if (!signal.aborted) this.motionReady = true;
    });
    root.dispatchEvent(new CustomEvent('meri:options:ready', { bubbles: true }));
  }

  // The rows a group shows: its add-ons, or a dropdown's box (its add-ons stay out of sight).
  rows(disclosure) {
    const fields = disclosure.querySelector('[data-options-choice]');
    if (!fields) return [];
    const row = fields.matches('[data-options-dropdown]') ? '.options-dropdown-field' : '.options-value';
    return [...fields.children].filter((node) => node.matches(row));
  }

  // 2026-11-04 (Owner): under a mouse a picture choice's frame lifts and leans toward the pointer, as the reference
  // recording's lifted chip does; leaving lets it settle back on the spring (CSS). A finger only presses it.
  setupLift(signal) {
    const reduced = matchMedia('(prefers-reduced-motion: reduce)');
    for (const frame of this.root.querySelectorAll('[data-options-presentation="image"] .options-item-frame')) {
      let tick = 0;
      const settle = () => {
        cancelAnimationFrame(tick);
        frame.removeAttribute('data-options-lift');
        frame.style.removeProperty('--options-tilt-x');
        frame.style.removeProperty('--options-tilt-y');
      };
      const card = frame.closest('.options-swatch');
      card.addEventListener(
        'pointermove',
        (event) => {
          if (event.pointerType !== 'mouse' || card.querySelector(':disabled') || reduced.matches) return settle();
          const { clientX, clientY } = event;
          cancelAnimationFrame(tick);
          tick = requestAnimationFrame(() => {
            // Measured from the card, which never leans, so the lean cannot feed back into itself.
            const box = card.getBoundingClientRect();
            const x = Math.max(-0.5, Math.min(0.5, (clientX - box.left - frame.offsetLeft) / frame.offsetWidth - 0.5));
            const y = Math.max(-0.5, Math.min(0.5, (clientY - box.top - frame.offsetTop) / frame.offsetHeight - 0.5));
            frame.toggleAttribute('data-options-lift', true);
            frame.style.setProperty('--options-tilt-x', `${(-y * 12).toFixed(2)}deg`);
            frame.style.setProperty('--options-tilt-y', `${(x * 12).toFixed(2)}deg`);
          });
        },
        { signal, passive: true },
      );
      card.addEventListener('pointerleave', settle, { signal });
      signal.addEventListener('abort', settle, { once: true });
    }
  }

  choices() {
    return [...this.root.querySelectorAll('[data-options-choice]')];
  }
  // 2.9.9: description windows are the shared <meri-help-window> (help-window.js), the group's and each add-on's; a
  // folding group closes them at once.
  closeHelp(now = false) {
    for (const panel of this.root.querySelectorAll('meri-help-window')) panel.close?.(now);
  }
  // 2.9.9 (Owner): an add-on variant's compare-at price shows struck through before its price, as Liquid renders it;
  // a price without one stays plain text. Returns whether the price changed.
  showPrice(target, label, original) {
    const struck = target.querySelector('[data-options-item-original]');
    const current = target.querySelector('[data-options-item-current]');
    const same = original
      ? struck?.lastChild?.textContent === original && current?.textContent === label
      : !struck && target.textContent === label;
    if (same) return false;
    if (!original) {
      target.textContent = label;
      return true;
    }
    const strike = document.createElement('s');
    strike.className = 'options-item-original';
    strike.dataset.optionsItemOriginal = '';
    const hidden = document.createElement('span');
    hidden.className = 'visually-hidden';
    hidden.textContent = (this.root.dataset.optionsRegular || '') + ' ';
    strike.append(hidden, original);
    const now = document.createElement('span');
    now.className = 'options-item-current price-sale';
    now.dataset.optionsItemCurrent = '';
    now.textContent = label;
    // The space keeps the two apart when read aloud, as in Liquid's markup; a flex line does not show it.
    target.replaceChildren(strike, ' ', now);
    return true;
  }
  // 2.9.9 (Owner): every add-on price keeps one line wherever it can, at its own size or else a little smaller; a
  // compare-at price then stacks, the original above the price, at the smaller size. A list row keeps the name and
  // the price on its first line, beside the choice and its picture, the same way, and a name too long even then wraps
  // beside the price ('wrap') rather than moving below and making the row taller. Only a row too narrow for that keeps
  // its lines as before. The row and the card keep their size.
  fitPrices() {
    for (const price of this.root.querySelectorAll('[data-options-price-fit]'))
      price.removeAttribute('data-options-price-fit');
    const original = (price) => price.querySelector('[data-options-item-original]');
    const current = (price) => price.querySelector('[data-options-item-current]');
    const pair = (price) => original(price) && current(price);
    // The price takes more than one line: the original above the price, or a plain price's own text wrapped.
    const apart = (price) => {
      if (pair(price)) return current(price).offsetTop > original(price).offsetTop + 1;
      const range = document.createRange();
      range.selectNodeContents(price);
      const tops = [...range.getClientRects()].map((rect) => rect.top);
      return tops.some((top) => top > tops[0] + 1);
    };
    // A list row's items share a centre line, so a price below its choice control is on a later line of the row.
    const below = (price) => {
      const first = !price.closest('[data-options-presentation="image"]') && price.parentElement.firstElementChild;
      return !!first && price.offsetTop >= first.offsetTop + first.offsetHeight - 1;
    };
    const crowded = (price) => apart(price) || below(price);
    const fit = (prices, size) => {
      for (const price of prices)
        if (size) price.dataset.optionsPriceFit = size;
        else price.removeAttribute('data-options-price-fit');
      return prices;
    };
    // Only a compare-at price has a stacked form, the original above the price.
    const stack = (prices, test) => fit(prices.filter(pair).filter(test), 'stacked');
    // A dropdown's own rows stay out of sight; its window and box lay out their prices themselves.
    let left = [...this.root.querySelectorAll('[data-options-item-price]')].filter(
      (price) => price.offsetParent && !price.closest('[data-options-dropdown]') && crowded(price),
    );
    stack(fit(left, 'small'), crowded);
    left = fit(left.filter(below), 'wrap');
    const under = fit(fit(left.filter(below), '').filter(apart), 'small');
    stack(under, apart);
    for (const { box } of this.dropdowns || []) this.fitDropdownPrice(box);
  }
  // 3.0.0 (Owner): a dropdown box keeps one line. A name cut short makes the price a little smaller, then stacks a
  // compare-at price, the original above the price, so the name has more room; the box keeps its size.
  fitDropdownPrice(box) {
    const price = box.querySelector('[data-options-dropdown-price]');
    const name = box.querySelector('[data-options-dropdown-name]');
    price.removeAttribute('data-options-price-fit');
    const cut = () => name.scrollWidth > name.clientWidth + 1;
    if (!box.offsetParent || !price.firstChild || !cut()) return;
    price.dataset.optionsPriceFit = 'small';
    if (cut() && price.querySelector('[data-options-item-original]')) price.dataset.optionsPriceFit = 'stacked';
  }
  // 2.9.8 (Owner): image choices share the row by their count and the width instead of a fixed card size. A wide
  // screen fits as many as it can at 6rem each, a phone at most three, and the rows are evened out: four on a phone
  // make two rows of two and five make three and two. The large-image size setting is the largest a card grows.
  layoutImages() {
    const phone = matchMedia('(max-width: 600px)').matches;
    const rem = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
    for (const fields of this.root.querySelectorAll('[data-options-choice][data-options-presentation="image"]')) {
      const count = [...fields.children].filter((node) => node.matches('[data-options-value]') && !node.hidden).length;
      const width = fields.clientWidth;
      if (!count || !width) continue;
      const gap = parseFloat(getComputedStyle(fields).columnGap) || 0;
      const most = phone ? 3 : Math.max(1, Math.floor((width + gap) / (6 * rem + gap)));
      fields.style.setProperty('--options-image-columns', String(Math.ceil(count / Math.ceil(count / most))));
    }
  }
  // 2.9.8 (Owner): an image choice shows its name in a small window by the picture, opened and closed like the
  // description window. A mouse shows it while over the picture; a finger's tap still selects the choice and also
  // shows the name with a close button; a touch anywhere else closes it. Keyboard focus shows it too.
  setupNames(signal) {
    const bubble = this.root.querySelector('[data-options-name-bubble]');
    if (!bubble || !('showPopover' in HTMLElement.prototype)) return;
    const text = bubble.querySelector('[data-options-name-text]');
    const reduced = matchMedia('(prefers-reduced-motion: reduce)');
    let card = null;
    let pointer = 'mouse';
    let timer = 0;
    let animation = null;
    let closing = false;
    let watch = null;
    const isOpen = () => bubble.matches(':popover-open');
    // Only a card whose picture hides its name, or (3.0.0) a card without a picture whose name is cut; any other card
    // already shows the whole name.
    const picture = (swatch) =>
      !swatch.closest('[data-options-image-failed]') && swatch.querySelector('img.options-item-image:not([hidden])');
    const pictured = (swatch) => {
      if (picture(swatch)) return true;
      const name = swatch.querySelector('.options-item-name');
      return !!name && name.scrollHeight > name.clientHeight + 1;
    };
    const place = () => {
      const viewport = window.visualViewport;
      const left = viewport?.offsetLeft || 0;
      const top = viewport?.offsetTop || 0;
      const width = viewport?.width || innerWidth;
      const rect = (picture(card) || card.querySelector('.options-item-name')).getBoundingClientRect();
      const centre = rect.left + rect.width / 2;
      const x = Math.max(left + 12, Math.min(centre - bubble.offsetWidth / 2, left + width - bubble.offsetWidth - 12));
      const above = rect.top - 10 - bubble.offsetHeight >= top + 12;
      bubble.dataset.side = above ? 'above' : 'below';
      bubble.style.left = x + 'px';
      bubble.style.top = (above ? rect.top - 10 - bubble.offsetHeight : rect.bottom + 10) + 'px';
      bubble.style.setProperty(
        '--options-name-arrow',
        Math.max(14, Math.min(bubble.offsetWidth - 14, centre - x)) + 'px',
      );
    };
    const show = (swatch, touch) => {
      clearTimeout(timer);
      const same = card === swatch && isOpen() && !closing;
      card = swatch;
      text.textContent = swatch.querySelector('.options-item-name')?.textContent.trim() || '';
      bubble.toggleAttribute('data-touch', touch);
      animation?.cancel();
      closing = false;
      if (!isOpen()) bubble.showPopover();
      place();
      if (!same) animation = growWindow(bubble, bubble.dataset.side);
    };
    const hide = (now = false) => {
      clearTimeout(timer);
      if (!isOpen() || (closing && !now)) return;
      animation?.cancel();
      if (now || reduced.matches) {
        closing = false;
        bubble.hidePopover();
        return;
      }
      closing = true;
      animation = shrinkWindow(bubble);
      animation.onfinish = () => {
        closing = false;
        animation.cancel();
        bubble.hidePopover();
      };
    };
    this.closeName = hide;
    signal.addEventListener(
      'abort',
      () => {
        clearTimeout(timer);
        animation?.cancel();
        watch?.abort();
      },
      { once: true },
    );
    bubble.addEventListener(
      'beforetoggle',
      (event) => {
        watch?.abort();
        watch = null;
        if (event.newState !== 'open' || signal.aborted) return;
        watch = new AbortController();
        const options = { signal: watch.signal };
        document.addEventListener(
          'pointerdown',
          (down) => {
            if (!bubble.contains(down.target) && !card?.contains(down.target)) hide();
          },
          { ...options, capture: true },
        );
        document.addEventListener('keydown', (key) => key.key === 'Escape' && hide(), options);
        document.addEventListener('scroll', (scroll) => !bubble.contains(scroll.target) && hide(true), {
          ...options,
          capture: true,
          passive: true,
        });
        window.addEventListener('resize', () => hide(true), { ...options, passive: true });
      },
      { signal },
    );
    bubble.querySelector('[data-options-name-close]')?.addEventListener('click', () => hide(), { signal });
    for (const swatch of this.root.querySelectorAll('[data-options-presentation="image"] .options-swatch')) {
      swatch.addEventListener('pointerdown', (event) => (pointer = event.pointerType || 'mouse'), { signal });
      swatch.addEventListener(
        'pointerenter',
        (event) => event.pointerType === 'mouse' && pictured(swatch) && show(swatch, false),
        { signal },
      );
      swatch.addEventListener(
        'pointerleave',
        (event) => {
          if (event.pointerType !== 'mouse') return;
          clearTimeout(timer);
          timer = setTimeout(hide, 150);
        },
        { signal },
      );
      // A finger's tap selects the choice as before (the label's own action) and shows its name.
      swatch.addEventListener('click', () => pointer !== 'mouse' && pictured(swatch) && show(swatch, true), {
        signal,
      });
      const input = swatch.querySelector('input');
      input?.addEventListener(
        'focus',
        () => input.matches(':focus-visible') && pictured(swatch) && show(swatch, false),
        {
          signal,
        },
      );
      input?.addEventListener('blur', () => hide(), { signal });
    }
  }
  setupVariantPopovers() {
    this.variantPopovers = [];
    this.root.querySelectorAll('[data-options-variant-ui]').forEach((node) => node.remove());
    if (!('showPopover' in HTMLElement.prototype)) return;
    const events = { signal: this.abort.signal };
    for (const select of this.root.querySelectorAll('[data-options-variant-label]')) {
      // A dropdown lists each add-on's variants in its own window.
      if (select.closest('[data-options-dropdown]')) continue;
      const value = select.closest('[data-options-value]');
      const control = value.querySelector('[data-options-trigger]');
      const card = value.querySelector('.options-swatch');
      if (!control || !card) continue;
      const create = (tag, className, text) => {
        const node = document.createElement(tag);
        node.className = className;
        if (text) node.textContent = text;
        return node;
      };
      const summary = create('span', 'options-variant-summary');
      summary.dataset.optionsVariantUi = '';
      card.append(summary);
      select.hidden = true;
      const label = select.previousElementSibling;
      if (label?.matches('label')) label.hidden = true;
      if (select.options.length <= 1) {
        this.variantPopovers.push({ select, control, summary, buttons: [] });
        continue;
      }
      const trigger = create('button', 'options-variant-toggle');
      trigger.type = 'button';
      trigger.dataset.optionsVariantUi = '';
      trigger.setAttribute('aria-label', `${control.value}: ${select.dataset.optionsVariantLabel}`);
      trigger.setAttribute('aria-haspopup', 'dialog');
      trigger.setAttribute('aria-expanded', 'false');
      const panel = create('div', 'options-variant-popover');
      panel.dataset.optionsVariantUi = '';
      panel.id = select.id + '-popover';
      panel.popover = 'auto';
      panel.setAttribute('role', 'dialog');
      panel.setAttribute('aria-label', trigger.getAttribute('aria-label'));
      trigger.setAttribute('aria-controls', panel.id);
      trigger.setAttribute('popovertarget', panel.id);
      const heading = create('p', 'options-variant-label', select.dataset.optionsVariantLabel);
      panel.append(heading);
      const buttons = [...select.options].map((option) => {
        const button = create('button', 'options-variant-option');
        button.type = 'button';
        button.disabled = option.disabled;
        const title = create('span', 'options-variant-name', option.dataset.variantTitle || option.label);
        const price = create(
          'span',
          'options-variant-price',
          Number(option.dataset.price) === 0 ? this.text('optionsFree') : option.dataset.priceLabel,
        );
        button.append(title);
        // 2.9.9 (Owner): a variant with a compare-at price shows it struck through before its price.
        if (option.dataset.compareLabel) {
          const original = create('s', 'options-variant-original', option.dataset.compareLabel);
          original.prepend(create('span', 'visually-hidden', (this.root.dataset.optionsRegular || '') + ' '));
          price.classList.add('price-sale');
          button.append(original, ' ');
        }
        button.append(price);
        button.addEventListener(
          'click',
          () => {
            if (control.disabled || option.disabled) return;
            if (!control.checked) {
              control.checked = true;
              control.dispatchEvent(new Event('input', { bubbles: true }));
            }
            if (!control.checked || select.disabled) return;
            select.value = option.value;
            select.dispatchEvent(new Event('change', { bubbles: true }));
            panel.hidePopover();
            trigger.focus({ preventScroll: true });
          },
          events,
        );
        panel.append(button);
        return button;
      });
      value.append(trigger, panel);
      value.classList.add('options-value--variants');
      const close = () => {
        if (panel.matches(':popover-open')) panel.hidePopover();
      };
      const open = () => {
        if (control.disabled || trigger.disabled) return;
        panel.showPopover();
        positionPopover(panel, trigger);
        unfoldPopover(panel, buttons);
        (buttons[select.selectedIndex] || buttons.find((button) => !button.disabled))?.focus({ preventScroll: true });
      };
      panel.addEventListener(
        'beforetoggle',
        (event) => {
          trigger.setAttribute('aria-expanded', String(event.newState === 'open'));
        },
        events,
      );
      trigger.addEventListener(
        'click',
        (event) => {
          event.preventDefault();
          if (panel.matches(':popover-open')) return close();
          open();
        },
        events,
      );
      panel.addEventListener(
        'keydown',
        (event) => {
          if (event.key === 'Escape') {
            event.preventDefault();
            close();
            trigger.focus({ preventScroll: true });
          } else if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
            event.preventDefault();
            const available = buttons.filter((button) => !button.disabled);
            const index = available.indexOf(document.activeElement);
            const next =
              event.key === 'Home'
                ? 0
                : event.key === 'End'
                  ? available.length - 1
                  : event.key === 'ArrowDown'
                    ? (index + 1) % available.length
                    : (index <= 0 ? available.length : index) - 1;
            available[next]?.focus();
          }
        },
        events,
      );
      panel.addEventListener(
        'focusout',
        (event) => {
          if (focusLeaves(panel, event.relatedTarget, trigger)) close();
        },
        events,
      );
      closeOnScrollWhileOpen(panel, close, events.signal, true);
      this.root.addEventListener(
        'toggle',
        (event) => {
          if (event.target.matches('[data-options-disclosure]') && !event.target.open) close();
        },
        { ...events, capture: true },
      );
      this.variantPopovers.push({ select, control, trigger, panel, summary, buttons, close, open });
    }
  }
  syncVariantPopovers() {
    for (const { select, control, trigger, summary, buttons, close } of this.variantPopovers || []) {
      if (trigger) trigger.disabled = control.disabled;
      if (control.disabled) close?.();
      const selected =
        select.selectedOptions[0] ||
        [...select.options].find((option) => option.value === select.dataset.optionsAutoVariant);
      summary.textContent = selected?.dataset.variantTitle || '';
      summary.hidden = !summary.textContent;
      buttons.forEach((button, index) =>
        button.setAttribute('aria-pressed', String(select.options[index] === selected)),
      );
    }
  }
  // 3.0.0 (Owner): a dropdown group is single choice shown as one box drawn like an add-on row. The box opens the
  // add-ons, each add-on's variants under its name, in a window drawn like the region and language selector: the same
  // motion, header, scroll arrows and keys. The single list's controls stay underneath, out of reach, and keep every
  // rule of the group; a choice here sets them as the list would. Without pop-ups the list itself shows instead.
  setupDropdowns() {
    this.dropdowns = [];
    this.root.querySelectorAll('[data-options-dropdown-ui]').forEach((node) => node.remove());
    const events = { signal: this.abort.signal };
    const create = (tag, className, text) => {
      const node = document.createElement(tag);
      node.className = className;
      if (text) node.textContent = text;
      return node;
    };
    for (const choice of this.root.querySelectorAll('[data-options-choice][data-options-dropdown]')) {
      const field = choice.querySelector(':scope > [data-options-dropdown-field]');
      const box = field?.querySelector('[data-options-dropdown-box]');
      const mark = field?.querySelector('[data-options-dropdown-mark]');
      if (!box || !mark || !('showPopover' in HTMLElement.prototype)) {
        choice.removeAttribute('data-options-dropdown');
        continue;
      }
      const heading = choice.dataset.optionsLabel;
      const values = [...choice.querySelectorAll(':scope > [data-options-value]')];
      const homes = new Map();
      for (const value of values) {
        value.inert = true;
        const help = value.querySelector('meri-help-window[data-options-item-help]');
        if (help) homes.set(help, value);
      }
      // A window an earlier setup moved beside the box goes back to its add-on.
      for (const help of field.querySelectorAll(':scope > meri-help-window')) {
        const home = values.find((value) =>
          value.querySelector('[data-options-trigger]')?.getAttribute('aria-describedby')?.split(' ').includes(help.id),
        );
        if (home) {
          home.append(help);
          homes.set(help, home);
        }
      }
      box.dataset.placeholder ||= box.querySelector('[data-options-dropdown-name]').textContent.trim();
      // 3.0.0 (Owner): an add-on without a picture shows its name only; among add-ons with pictures its row keeps the
      // picture's space, so the names stay in line, and the box shows only its name.
      const pictured = values.some((value) => value.querySelector('img.options-item-image'));
      const panel = create('div', 'options-dropdown');
      panel.dataset.optionsDropdownUi = '';
      panel.toggleAttribute('data-options-dropdown-pictured', pictured);
      panel.id = `options-dropdown-${crypto.randomUUID()}`;
      panel.popover = 'auto';
      panel.setAttribute('role', 'dialog');
      panel.setAttribute('aria-label', heading);
      box.setAttribute('aria-controls', panel.id);
      box.setAttribute('popovertarget', panel.id);
      const header = create('div', 'options-dropdown__header');
      const closeButton = create('button', 'options-dropdown__close', '×');
      closeButton.type = 'button';
      closeButton.setAttribute('aria-label', this.text('optionsClose'));
      header.append(create('span', 'options-dropdown__title', heading), closeButton);
      const list = create('div', 'options-dropdown__list');
      list.setAttribute('role', 'group');
      list.setAttribute('aria-label', heading);
      const rows = [];
      for (const value of values) {
        const control = value.querySelector('[data-options-trigger]');
        if (!control) continue;
        const select = value.querySelector('[data-options-addon-variant]');
        const options = select ? [...select.options] : [];
        const picture = value.querySelector('img.options-item-image');
        const thumb = () => {
          if (!picture) {
            if (!pictured) return [];
            const space = create('span', 'options-dropdown__thumb');
            space.setAttribute('aria-hidden', 'true');
            return [space];
          }
          const node = picture.cloneNode(true);
          node.removeAttribute('id');
          node.removeAttribute('hidden');
          node.className = 'options-dropdown__thumb';
          return [node];
        };
        const reason = create('span', 'options-dropdown__reason');
        reason.id = `${panel.id}-reason-${rows.length}`;
        const help = value.querySelector('meri-help-window[data-options-item-help]');
        const row = (button, option, variant) => {
          button.type = 'button';
          button.addEventListener('click', () => choose(entry), events);
          list.append(button);
          const entry = { button, control, select, option, value, reason, help, picture, name: control.value, variant };
          rows.push(entry);
        };
        // The spaces keep the parts apart when read aloud; a flex row does not show them.
        const text = create('span', 'options-dropdown__text');
        text.append(create('span', 'options-dropdown__name', control.value), ' ', reason);
        if (options.length > 1) {
          // A product with variants: its name, then each variant with its own price.
          const product = create('div', 'options-dropdown__product');
          product.append(...thumb(), text);
          list.append(product);
          for (const option of options) {
            const button = create('button', 'options-dropdown__option options-dropdown__option--variant');
            const title = option.dataset.variantTitle || option.label;
            const label = create('span', 'options-dropdown__text');
            label.append(create('span', 'visually-hidden', `${control.value}: `), title);
            button.append(label, ' ', this.dropdownPrice(option, value));
            button.setAttribute('aria-describedby', reason.id);
            row(button, option, title);
          }
        } else {
          const button = create('button', 'options-dropdown__option');
          button.append(...thumb(), text, ' ', this.dropdownPrice(options[0], value));
          row(button, options[0], '');
        }
      }
      panel.append(header, list);
      field.append(panel);
      const close = () => {
        if (panel.matches(':popover-open')) panel.hidePopover();
      };
      const open = () => {
        if (panel.matches(':popover-open')) return;
        panel.showPopover();
        const mobile = matchMedia('(max-width: 760px)').matches;
        positionPopover(panel, box, Math.max(box.offsetWidth, 280), mobile ? 360 : Infinity);
        unfoldPopover(panel, list.children);
        const enabled = rows.filter((entry) => !entry.button.disabled);
        const current = enabled.find((entry) => entry.button.getAttribute('aria-pressed') === 'true');
        ((current || enabled[0])?.button || closeButton).focus({ preventScroll: true });
      };
      const choose = (entry) => {
        const { control, select, option } = entry;
        if (entry.button.disabled) return;
        if (this.dropdownChosen(entry)) {
          // A second press on the chosen add-on clears an optional group, as the list does.
          if (choice.dataset.optionsRequired !== 'true') {
            control.checked = false;
            control.dispatchEvent(new Event('input', { bubbles: true }));
            control.dispatchEvent(new Event('change', { bubbles: true }));
          }
        } else {
          if (!control.checked) {
            control.checked = true;
            control.dispatchEvent(new Event('input', { bubbles: true }));
          }
          if (control.checked && select && option && !select.disabled && select.value !== option.value) {
            select.value = option.value;
            select.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }
        close();
        box.focus({ preventScroll: true });
      };
      panel.addEventListener(
        'beforetoggle',
        (event) => box.setAttribute('aria-expanded', String(event.newState === 'open')),
        events,
      );
      box.addEventListener(
        'click',
        (event) => {
          event.preventDefault();
          if (panel.matches(':popover-open')) close();
          else open();
        },
        events,
      );
      box.addEventListener(
        'keydown',
        (event) => {
          if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
          event.preventDefault();
          open();
        },
        events,
      );
      closeButton.addEventListener(
        'click',
        () => {
          close();
          box.focus({ preventScroll: true });
        },
        events,
      );
      panel.addEventListener(
        'keydown',
        (event) => {
          if (event.key === 'Escape') {
            event.preventDefault();
            close();
            box.focus({ preventScroll: true });
          } else if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
            event.preventDefault();
            const available = rows.map((entry) => entry.button).filter((button) => !button.disabled);
            const index = available.indexOf(document.activeElement);
            const next =
              event.key === 'Home'
                ? 0
                : event.key === 'End'
                  ? available.length - 1
                  : event.key === 'ArrowDown'
                    ? (index + 1) % available.length
                    : (index <= 0 ? available.length : index) - 1;
            available[next]?.focus();
          }
        },
        events,
      );
      panel.addEventListener(
        'focusout',
        (event) => {
          if (focusLeaves(panel, event.relatedTarget, box)) close();
        },
        events,
      );
      closeOnScrollWhileOpen(panel, close, events.signal, true);
      this.root.addEventListener(
        'toggle',
        (event) => {
          if (event.target.matches('[data-options-disclosure]') && !event.target.open) close();
        },
        { ...events, capture: true },
      );
      this.dropdowns.push({ choice, field, box, mark, panel, rows, homes, close, open });
    }
  }
  // The chosen add-on, and for a product with variants the chosen variant, as the group counts it.
  dropdownChosen({ control, select, option }) {
    if (!control.checked || control.disabled) return false;
    return !select || select.options.length <= 1 || select.value === option?.value;
  }
  // A row's price as the add-on's own row shows it: a compare-at price struck through before it.
  dropdownPrice(option, value) {
    const price = document.createElement('span');
    price.className = 'options-dropdown__price';
    if (!option) {
      // An add-on without a usable variant keeps the note its row shows.
      price.textContent = value.querySelector('[data-options-item-price]')?.textContent.trim() || '';
      return price;
    }
    const amount = Number(option.dataset.price);
    const label =
      (amount > 0 ? '＋' : '') +
      (amount === 0 ? this.text('optionsFree') : option.dataset.priceLabel || '') +
      (option.dataset.unitPrice ? ` — ${option.dataset.unitPrice}` : '');
    if (!option.dataset.compareLabel) {
      price.textContent = label;
      return price;
    }
    const original = document.createElement('s');
    original.className = 'options-variant-original';
    const hidden = document.createElement('span');
    hidden.className = 'visually-hidden';
    hidden.textContent = (this.root.dataset.optionsRegular || '') + ' ';
    original.append(hidden, option.dataset.compareLabel);
    const now = document.createElement('span');
    now.className = 'price-sale';
    now.textContent = label;
    price.append(original, ' ', now);
    return price;
  }
  syncDropdowns() {
    for (const { choice, field, box, mark, rows, homes } of this.dropdowns || []) {
      const chosen = rows.find((entry) => this.dropdownChosen(entry)) || null;
      for (const entry of rows) {
        const locked = entry.control.disabled || Boolean(entry.option?.disabled);
        if (entry.button.disabled !== locked) entry.button.disabled = locked;
        entry.button.setAttribute('aria-pressed', String(entry === chosen));
        // A locked add-on says why, as its "!" note does in the list.
        const note = entry.value.querySelector('[data-options-conflict-reason]');
        const text = note && !note.hidden ? (note.querySelector('[data-options-reason-text]') || note).textContent : '';
        if (entry.reason.textContent !== text) entry.reason.textContent = text;
      }
      const chosenName = chosen?.variant ? `${chosen.name} — ${chosen.variant}` : chosen?.name;
      const name = chosenName || box.dataset.placeholder;
      const nameNode = box.querySelector('[data-options-dropdown-name]');
      let changed = false;
      if (nameNode.textContent !== name) {
        nameNode.textContent = name;
        changed = true;
        if (this.motionReady) roll(nameNode);
      }
      box.toggleAttribute('data-options-dropdown-empty', !chosen);
      const image = box.querySelector('[data-options-dropdown-image]');
      const pictureKey = chosen?.picture ? chosen.value.dataset.optionsValueId : '';
      if (image.dataset.for !== pictureKey) {
        const picture = chosen?.picture?.cloneNode(true);
        picture?.removeAttribute('id');
        picture?.removeAttribute('hidden');
        image.replaceChildren(...(picture ? [picture] : []));
        image.hidden = !picture;
        image.dataset.for = pictureKey;
        changed = true;
      }
      // The box shows the chosen add-on's own price, kept current by showPrice.
      const price = box.querySelector('[data-options-dropdown-price]');
      const source = chosen?.value.querySelector('[data-options-item-price]');
      if ((source?.textContent || '') !== price.textContent) {
        price.replaceChildren(...(source ? [...source.childNodes].map((node) => node.cloneNode(true)) : []));
        changed = true;
        if (this.motionReady) roll(price);
      }
      const label = `${choice.dataset.optionsLabel}: ${name}${source ? ` ${source.textContent.replace(/\s+/g, ' ').trim()}` : ''}`;
      if (box.getAttribute('aria-label') !== label) box.setAttribute('aria-label', label);
      // The chosen add-on's own description opens from the mark beside the box.
      const help = chosen?.help || null;
      const shown = field.querySelector(':scope > meri-help-window');
      if (shown !== help) {
        if (shown) {
          shown.close?.(true);
          homes.get(shown)?.append(shown);
        }
        if (help) field.append(help);
      }
      if (mark.hidden !== !help) {
        mark.hidden = !help;
        changed = true;
      }
      if (help) mark.setAttribute('aria-label', mark.dataset.label.replace('__ITEM__', chosen.name));
      // The mark takes room from the box, so the price is fitted once everything beside the name is in place.
      if (changed) this.fitDropdownPrice(box);
    }
  }
  reveal(node, focus = true) {
    // A dropdown's controls are out of reach; its box takes the focus.
    node = node?.closest?.('[data-options-dropdown]')?.querySelector('[data-options-dropdown-box]') || node;
    const disclosures = [];
    for (let parent = node; parent && parent !== this.root; parent = parent.parentElement)
      if (parent.matches('details')) {
        // Invalid controls must be visible before the browser moves focus.
        // A growing, clipped details-content can otherwise reject that focus.
        if (focus && !parent.open) {
          parent.setAttribute('data-options-revealing', '');
          disclosures.push(parent);
        }
        parent.open = true;
      }
    if (disclosures.length)
      requestAnimationFrame(() =>
        disclosures.forEach((disclosure) => disclosure.removeAttribute('data-options-revealing')),
      );
    if (!focus) return;
    // 3.0.0 (Owner): one attempt can stop at several groups, each answering in turn; the first problem on the page
    // takes the focus without a jump, while the page glides to it (flagProblems). The browser runs pending work between
    // its own invalid events, so the first problem is kept until the attempt is over.
    const state = this.state;
    if (!state.focusTarget || state.focusTarget.compareDocumentPosition(node) & Node.DOCUMENT_POSITION_PRECEDING)
      state.focusTarget = node;
    state.focusEnd ||= setTimeout(() => {
      state.focusTarget = null;
      state.focusEnd = 0;
    });
    if (state.focusQueued) return;
    state.focusQueued = true;
    queueMicrotask(() => {
      state.focusQueued = false;
      const target = state.focusTarget;
      if (target?.isConnected && document.activeElement !== target) target.focus?.({ preventScroll: true });
    });
  }
  // 3.0.0 (Owner): what stops add to cart in this group. A required group without a choice is marked by its frame (a
  // dropdown, by its box), and so is the choice in its set that excludes all its add-ons; an add-on that cannot be
  // bought as chosen is marked by its own row.
  problems() {
    const marks = [];
    const frame = this.root.querySelector('.options-group-frame');
    // A dropdown's add-ons are out of sight; its box stands for them.
    const row = (value) =>
      value.closest('[data-options-dropdown]')?.querySelector('[data-options-dropdown-box]') ||
      value.querySelector('.options-swatch') ||
      value;
    if (this.emptyChoice || this.exclusionError) marks.push(frame);
    const entries = exclusionStates.get(this.optionSet || this.root)?.entries || [];
    for (const choice of this.choices()) {
      if (hiddenByBranch(choice)) continue;
      const triggers = this.triggers(choice);
      const chosen = triggers.filter((trigger) => trigger.checked && !trigger.disabled);
      if (choice.dataset.optionsRequired === 'true' && !chosen.length) {
        marks.push(
          (choice.matches('[data-options-dropdown]') && choice.querySelector('[data-options-dropdown-box]')) || frame,
        );
        const sides = new Set(
          entries
            .filter((entry) => triggers.includes(entry.trigger) && entry.trigger.hasAttribute('data-options-excluded'))
            .map((entry) => entry.side),
        );
        for (const entry of entries)
          if (sides.size && entry.side && !sides.has(entry.side) && entry.trigger.checked && !entry.trigger.disabled)
            marks.push(row(entry.value));
      }
      for (const trigger of chosen) {
        const value = trigger.closest('[data-options-value]');
        const broken =
          trigger === this.invalidAccessory ||
          [...value.querySelectorAll('input, select, textarea')].some(
            (control) => control !== trigger && !control.disabled && !control.validity.valid,
          );
        if (broken) marks.push(row(value));
      }
    }
    return marks.filter(Boolean);
  }
  // 3.0.0 (Owner): a blocked add to cart or Buy it now frames every problem of the product in red, glides the page up
  // to the first one and, once there, shakes each group with a problem twice in about half a second. With reduced
  // motion the page jumps there and nothing shakes. A frame stays until its problem is put right (refreshProblems).
  flagProblems() {
    const state = this.state;
    // Every group an attempt stops at asks in turn; the attempt is flagged once, when the first one asks.
    if (state.flagging) return;
    state.feedback?.abort();
    const feedback = (state.feedback = new AbortController());
    let pending;
    feedback.signal.addEventListener(
      'abort',
      () => {
        clearTimeout(state.flagging);
        state.flagging = 0;
        clearTimeout(pending);
        state.shakes?.forEach((animation) => animation.cancel());
        state.shakes = [];
      },
      { once: true },
    );
    state.flagging = setTimeout(() => (state.flagging = 0));
    queueMicrotask(() => {
      if (feedback.signal.aborted) return;
      const marks = [...new Set([...state.groups].flatMap((group) => group.problems()))]
        .filter((node) => node.isConnected)
        .sort((a, b) => (a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1));
      if (!marks.length) return;
      state.problems ||= new Set();
      for (const node of marks) {
        node.setAttribute('data-options-problem', '');
        state.problems.add(node);
      }
      const frames = [...new Set(marks.map((node) => node.closest('.options-group-frame')).filter(Boolean))];
      const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
      state.shakes?.forEach((animation) => animation.cancel());
      state.shakes = [];
      // The glide waits for the attempt to finish: the messages each group writes above can still move the page, and
      // Safari stops a glide that such a move interrupts.
      pending = setTimeout(() => {
        if (feedback.signal.aborted) return;
        glide(frames[0] || marks[0], reduced, feedback.signal).then(() => {
          if (reduced || feedback.signal.aborted) return;
          state.shakes = frames
            .filter((frame) => frame.isConnected && frame.animate)
            .map((frame) =>
              frame.animate(
                [
                  { translate: '0' },
                  { translate: '-0.5rem 0', offset: 0.12 },
                  { translate: '0.5rem 0', offset: 0.36 },
                  { translate: '-0.375rem 0', offset: 0.6 },
                  { translate: '0.25rem 0', offset: 0.82 },
                  { translate: '0' },
                ],
                { duration: 500, easing: 'ease-out' },
              ),
            );
        });
      });
    });
  }
  // A framed problem that has been put right loses its frame at once; a new problem waits for the next attempt.
  refreshProblems() {
    const framed = this.state.problems;
    if (!framed?.size) return;
    const current = new Set([...this.state.groups].flatMap((group) => group.problems()));
    for (const node of framed)
      if (!current.has(node) || !node.isConnected) {
        node.removeAttribute('data-options-problem');
        framed.delete(node);
      }
  }
  selectionRules(groups, changed) {
    // The DOM object, not a reusable block ID or product ID, owns interaction state.
    const scope = this.optionSet || this.root;
    let state = exclusionStates.get(scope);
    if (!state) {
      const ids = (value) =>
        new Set(
          (value || '')
            .split(',')
            .map((id) => id.trim())
            .filter(Boolean),
        );
      const a = ids(scope.dataset.optionsExclusionA),
        b = ids(scope.dataset.optionsExclusionB);
      // 2.9.9 (Owner): trigger add-ons unlock the dependent add-ons, which stay visible but locked until then.
      const sources = ids(scope.dataset.optionsTriggerA),
        dependents = ids(scope.dataset.optionsTriggerB);
      const triggers = [...scope.querySelectorAll('[data-options-trigger]')].filter(
        (trigger) => (trigger.closest('[data-options-set]') || trigger.closest('meri-options')) === scope,
      );
      const entries = triggers.map((trigger) => {
        const value = trigger.closest('[data-options-value]');
        const id = value.dataset.optionsProduct;
        return {
          trigger,
          value,
          id,
          side: a.has(id) ? 'a' : b.has(id) ? 'b' : '',
          // An add-on listed on both sides is misconfigured; it stays locked like any dependent.
          role: dependents.has(id) ? 'dependent' : sources.has(id) ? 'source' : '',
          unavailable: value.hasAttribute('data-options-value-unavailable'),
        };
      });
      const present = new Set(entries.map((entry) => entry.id));
      const duplicates = [...a].filter((id) => b.has(id));
      const missing = [...a, ...b].filter((id) => !present.has(id));
      const invalid =
        Number(scope.dataset.optionsExclusionInvalid || 0) > 0 ||
        duplicates.length ||
        missing.length ||
        Boolean(a.size) !== Boolean(b.size);
      const names = duplicates.map((id) => entries.find((entry) => entry.id === id)?.trigger.value || id);
      const details = names.length ? names.join(', ') : scope.dataset.optionsExclusionNames || missing.join(', ');
      const sideOf = (id) => (a.has(id) ? 'a' : b.has(id) ? 'b' : '');
      const linkedTwice = [...sources].filter((id) => dependents.has(id));
      const linkMissing = [...sources, ...dependents].filter((id) => !present.has(id));
      // A dependent whose every trigger sits on the other side of the exclusion could never be chosen.
      const unreachable = [...dependents].filter(
        (id) => sideOf(id) && [...sources].every((source) => sideOf(source) && sideOf(source) !== sideOf(id)),
      );
      const linkInvalid =
        Number(scope.dataset.optionsTriggerInvalid || 0) > 0 ||
        linkedTwice.length ||
        linkMissing.length ||
        unreachable.length ||
        Boolean(sources.size) !== Boolean(dependents.size);
      const linkNames = [...linkedTwice, ...unreachable].map(
        (id) => entries.find((entry) => entry.id === id)?.trigger.value || id,
      );
      state = {
        link: {
          enabled: Boolean(sources.size || dependents.size),
          invalid: Boolean(linkInvalid),
          error: (scope.dataset.optionsTriggerError || this.text('optionsUnavailable'))
            .replace('__SET__', scope.dataset.optionsSetName || '')
            .replace(
              '__ITEMS__',
              linkNames.length ? linkNames.join(', ') : scope.dataset.optionsTriggerNames || linkMissing.join(', '),
            ),
          requires: (scope.dataset.optionsRequires || this.text('optionsUnavailable')).replace(
            '__ITEMS__',
            entries
              .filter((entry) => entry.role === 'source')
              .map((entry) => entry.trigger.value)
              .join(' / '),
          ),
        },
        entries,
        enabled: Boolean(a.size && b.size),
        invalid: Boolean(invalid),
        notice: '',
        error: (scope.dataset.optionsExclusionError || this.text('optionsUnavailable'))
          .replace('__SET__', scope.dataset.optionsSetName || '')
          .replace('__ITEMS__', details),
        required: scope.dataset.optionsExclusionRequired || this.text('optionsRequired'),
        choices: [...scope.querySelectorAll('[data-options-choice][data-options-required="true"]')].filter(
          (choice) => (choice.closest('[data-options-set]') || choice.closest('meri-options')) === scope,
        ),
      };
      exclusionStates.set(scope, state);
    }
    const { entries, link } = state;
    // 2.9.9 (Owner): the exclusion acts only after a choice; neither side is locked in advance. Only a set where
    // each side on its own leaves some required group without a choice is a configuration error.
    const viable = (side) =>
      state.choices.every((choice) =>
        entries.some(
          (entry) =>
            entry.trigger.closest('[data-options-choice]') === choice &&
            !entry.unavailable &&
            (!entry.side || entry.side === side),
        ),
      );
    const unsatisfiable = state.enabled && !viable('a') && !viable('b');
    if (changed?.matches?.('[data-options-trigger]')) state.notice = '';
    // A misconfigured trigger rule keeps its dependents locked; the editor names the problem.
    const unlocked = () => !link.invalid && entries.some((entry) => entry.role === 'source' && entry.trigger.checked);
    let activeA = false;
    let activeB = false;
    // Exclusion and trigger rules can each clear a choice the other relies on, so apply both until nothing changes.
    for (let pass = 0; pass < 4; pass++) {
      const before = entries.map((entry) => entry.trigger.checked);
      const open = unlocked();
      for (const entry of entries) {
        if (
          entry.unavailable ||
          (state.enabled && (state.invalid || (unsatisfiable && entry.side))) ||
          (link.enabled && entry.role === 'dependent' && !open)
        )
          entry.trigger.checked = false;
      }
      activeA = entries.some((entry) => entry.side === 'a' && entry.trigger.checked);
      activeB = entries.some((entry) => entry.side === 'b' && entry.trigger.checked);
      if (activeA && activeB) {
        const preferred = entries.find((entry) => entry.trigger === changed && entry.trigger.checked)?.side;
        for (const entry of entries) if (entry.side && entry.side !== preferred) entry.trigger.checked = false;
        if (!preferred) state.notice = scope.dataset.optionsExclusionReset || this.text('optionsError');
        activeA = preferred === 'a';
        activeB = preferred === 'b';
      }
      if (entries.every((entry, index) => entry.trigger.checked === before[index])) break;
    }
    const open = unlocked();
    for (const group of groups)
      group.exclusionError = state.invalid ? state.error : unsatisfiable ? state.required : '';
    const message = scope.querySelector('[data-options-set-message]');
    if (message) {
      const editor = scope.dataset.optionsEditor === 'true';
      const text = [
        state.invalid
          ? editor
            ? state.error
            : this.text('optionsUnavailable')
          : unsatisfiable
            ? state.required
            : state.notice,
        link.invalid && editor ? link.error : '',
      ]
        .filter(Boolean)
        .join(' ');
      if (message.textContent !== text) message.textContent = text;
      message.hidden = !text;
    }
    const selected = entries.find((entry) => entry.trigger.checked && entry.side);
    for (const entry of entries) {
      const opposite = state.enabled && ((entry.side === 'a' && activeB) || (entry.side === 'b' && activeA));
      const impossible = Boolean(unsatisfiable && entry.side);
      const locked = link.enabled && entry.role === 'dependent' && !open;
      const blocked = Boolean(entry.unavailable || opposite || impossible || locked || (state.invalid && entry.side));
      // Marks add-ons that only the shopper's own choice excludes, so a required group it empties can say so.
      const excluded = Boolean(opposite && !entry.unavailable && !state.invalid);
      if (blocked) entry.trigger.checked = false;
      if (entry.trigger.hasAttribute('data-options-blocked') !== blocked)
        entry.trigger.toggleAttribute('data-options-blocked', blocked);
      if (entry.trigger.hasAttribute('data-options-locked') !== locked)
        entry.trigger.toggleAttribute('data-options-locked', locked);
      if (entry.trigger.hasAttribute('data-options-excluded') !== excluded)
        entry.trigger.toggleAttribute('data-options-excluded', excluded);
      const note = entry.value.querySelector('[data-options-conflict-reason]');
      if (!note) continue;
      const text =
        entry.unavailable || state.invalid
          ? blocked
            ? this.text('optionsUnavailable')
            : ''
          : impossible
            ? state.required
            : opposite
              ? this.text('optionsConflict').replace('__ITEM__', selected?.trigger.value || '')
              : locked
                ? link.invalid
                  ? this.text('optionsUnavailable')
                  : link.requires
                : '';
      const reasonText = note.querySelector('[data-options-reason-text]') || note;
      if (reasonText.textContent !== text) reasonText.textContent = text;
      const visible = Boolean(text);
      if (!visible && note.matches(':popover-open')) note.hidePopover();
      note.hidden = !visible;
      const button = entry.value.querySelector('[data-options-reason-button]');
      if (button) {
        button.hidden = !visible;
        if (button.getAttribute('aria-label') !== text) button.setAttribute('aria-label', text);
      }
    }
  }
  triggers(choice) {
    return [...choice.querySelectorAll('[data-options-trigger]')].filter(
      (node) => node.closest('[data-options-choice]') === choice,
    );
  }
  label(node) {
    return propertyLabel([this.propertyTitle || this.root.dataset.optionsTitle, node.dataset.optionsLabel]);
  }
  text(key) {
    return this.root.dataset[key] || this.root.dataset.optionsError || 'Please check your selections.';
  }
  syncAll(changed) {
    const groups = [...this.state.groups].sort((a, b) =>
      a.root === b.root ? 0 : a.root.compareDocumentPosition(b.root) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1,
    );
    for (const group of groups) {
      const title = (candidate) => {
        const name = candidate.root.dataset.optionsTitle;
        const setName = candidate.optionSet?.dataset.optionsSetName;
        return setName && setName.trim() !== name.trim() ? propertyLabel([setName, name]) : name;
      };
      const peers = groups
        .filter((other) => title(other) === title(group))
        .sort((a, b) => (a.root.dataset.optionsIdentity || '').localeCompare(b.root.dataset.optionsIdentity || ''));
      group.propertyTitle = title(group) + (peers.length > 1 ? ` (${peers.indexOf(group) + 1})` : '');
    }
    const affected = groups.filter((group) => (this.optionSet ? group.optionSet === this.optionSet : group === this));
    this.selectionRules(affected, changed);
    for (const group of affected) group.sync();
    for (const group of affected)
      group.selectedTriggers = new WeakSet([...group.root.querySelectorAll('[data-options-trigger]:checked')]);
    this.syncCosts(groups, affected);
    for (const group of affected) {
      // 2.9.9: a required group whose add-ons all wait for a trigger asks for the trigger, not for the store.
      const waiting = group
        .choices()
        .filter(
          (choice) =>
            !hiddenByBranch(choice) &&
            choice.dataset.optionsRequired === 'true' &&
            group.triggers(choice).length &&
            group.triggers(choice).every((t) => t.disabled) &&
            group
              .triggers(choice)
              .some((t) => t.hasAttribute('data-options-locked') && !t.hasAttribute('data-options-excluded')),
        );
      group.lockedMessage = waiting.length
        ? exclusionStates.get(group.optionSet || group.root)?.link?.requires || group.text('optionsRequired')
        : '';
      // 2.9.9 (Owner): a required group emptied by the shopper's own choice asks for another combination at add to
      // cart; its add-ons already say which choice excludes them.
      const conflicted = group
        .choices()
        .filter(
          (choice) =>
            !waiting.includes(choice) &&
            !hiddenByBranch(choice) &&
            choice.dataset.optionsRequired === 'true' &&
            group.triggers(choice).length &&
            group.triggers(choice).every((t) => t.disabled) &&
            group.triggers(choice).some((t) => t.hasAttribute('data-options-excluded')),
        );
      group.conflictMessage = conflicted.length
        ? exclusionStates.get(group.optionSet || group.root)?.required || group.text('optionsRequired')
        : '';
      const emptyChoice = group
        .choices()
        .some(
          (choice) =>
            !waiting.includes(choice) &&
            !conflicted.includes(choice) &&
            !hiddenByBranch(choice) &&
            (!group.triggers(choice).length ||
              (choice.dataset.optionsRequired === 'true' && group.triggers(choice).every((t) => t.disabled))),
        );
      group.emptyChoice = emptyChoice;
      group.configurationMessage = emptyChoice
        ? group.text(group.root.dataset.optionsEditor === 'true' ? 'optionsEditorEmpty' : 'optionsUnavailable')
        : '';
      const invalid = [...group.root.querySelectorAll('input, select, textarea')].some(
        (input) => !input.disabled && !input.validity.valid,
      );
      group.purchaseInvalid =
        invalid ||
        group.emptyChoice ||
        Boolean(group.exclusionError) ||
        Boolean(group.lockedMessage) ||
        Boolean(group.conflictMessage) ||
        group.variantAvailable === false;
      if (group.message) group.message.textContent = group.configurationMessage;
    }
    const nativeSafe = this.syncNativeCheckout(groups);
    for (const group of groups) {
      group.root.toggleAttribute(
        'data-options-cart-only',
        Boolean(group.purchaseInvalid || (!nativeSafe && group === groups[0])),
      );
      group.root.toggleAttribute('data-options-invalid', Boolean(group.purchaseInvalid));
    }
    // 3.0.0 (Owner): while the add-ons are incomplete, Buy it now stays in sight, faded, under a cover that runs the
    // add-to-cart checks (see the constructor); its payment button stays out of reach.
    const wallet = this.form.querySelector('[data-accelerated-wallet]');
    const incomplete = groups.some((group) => group.purchaseInvalid);
    if (wallet && wallet.inert !== incomplete) wallet.inert = incomplete;
    this.refreshProblems();
  }
  syncNativeCheckout(groups) {
    const state = this.state,
      values = new Map();
    const results = groups.map((group) => group.collectItems());
    const items = results.flatMap((result) => result.items);
    let safe = results.every((result) => !result.errors.length);
    if (safe && items.length && this.form.querySelector('[data-accelerated-checkout]')) {
      if (!state.prepareItems) {
        safe = false;
        if (!state.nativeLoading) {
          state.nativeLoading = import(globalThis.MeriAssets?.cart || './cart.js')
            .then(({ prepareItems }) => {
              state.prepareItems = prepareItems;
              state.groups.values().next().value?.syncAll();
            })
            .catch(() => {
              // Never drop paid accessories if native preparation fails.
              // Add to cart retains its existing loading and recovery path.
            });
        }
      } else {
        try {
          const parent = {
            id: this.form.elements.namedItem('id')?.value,
            quantity: this.form.elements.namedItem('quantity')?.value || 1,
            properties: { _meri_configuration: state.identity },
          };
          const publicValues = [...new FormData(this.form)].filter(([name]) => /^properties\[[^_]/.test(name));
          const signature = JSON.stringify([parent, items, publicValues]);
          if (signature !== state.nativeSignature) {
            state.nativeSignature = signature;
            state.nativeIdentity = crypto.randomUUID();
          }
          const prepared = state.prepareItems([parent, ...items], state.nativeIdentity);
          this.ensureIdentity();
          for (const [name, value] of Object.entries(prepared[0].properties))
            if (name !== '_meri_configuration') values.set('properties[' + name + ']', value);
          prepared.slice(1).forEach((item, index) => {
            const prefix = 'shopify-additional-items[' + (index + 1) + ']';
            for (const [name, value] of Object.entries(item)) {
              // The accelerated-checkout contract takes one JSON key/value list,
              // unlike Ajax Cart's nested multipart properties fields.
              values.set(
                prefix + '[' + name + ']',
                name === 'properties'
                  ? JSON.stringify(Object.entries(value).map(([key, value]) => ({ key, value })))
                  : String(value),
              );
            }
          });
        } catch {
          safe = false;
        }
      }
    }
    // Reconcile hidden fields only; keep the form and payment element.
    const fields = new Map(
      [...this.form.querySelectorAll('[data-options-native-checkout]')].map((field) => [field.name, field]),
    );
    for (const [name, value] of values) {
      let field = fields.get(name);
      if (!field) {
        field = document.createElement('input');
        field.type = 'hidden';
        field.name = name;
        field.dataset.optionsNativeCheckout = '';
        this.form.append(field);
      }
      if (field.value !== value) field.value = value;
      fields.delete(name);
    }
    fields.forEach((field) => field.remove());
    return safe;
  }
  syncCosts(groups, affected = groups) {
    let total = 0;
    const quantity = Number(this.form.elements.namedItem('quantity')?.value || 1);
    for (const group of groups) {
      if (!affected.includes(group) && group.subtotal !== undefined) {
        total += group.subtotal;
        continue;
      }
      let subtotal = 0;
      for (const addon of group.root.querySelectorAll('[data-options-addon]')) {
        const select = addon.querySelector('[data-options-addon-variant]');
        if (!select || select.disabled || !select.value) continue;
        const price = Number(select.selectedOptions[0]?.dataset.price);
        const count = Number(addon.querySelector('[data-options-addon-quantity]')?.value);
        if (Number.isSafeInteger(price) && Number.isSafeInteger(count) && price >= 0 && count > 0)
          subtotal += price * count;
      }
      group.subtotal = subtotal;
      total += subtotal;
      const format = (amount) => {
        try {
          return currencyFormat(group.root.dataset.optionsCurrency, amount);
        } catch {
          return '';
        }
      };
      const cost = group.root.querySelector('[data-options-group-cost]');
      const costText = group.text('optionsGroupTotal').replace('__AMOUNT__', format(subtotal));
      if (cost && cost.textContent !== costText) {
        cost.textContent = costText;
        if (group.motionReady) roll(cost);
      }
      group.formatAmount = format;
    }
    const last = groups.at(-1);
    for (const group of groups) {
      const node = group.root.querySelector('[data-options-all-cost]');
      if (node) {
        node.hidden = group !== last;
        const parts = group.text('optionsTotal').replace('__QUANTITY__', String(quantity)).split('__AMOUNT__');
        const formatted = group.formatAmount(total);
        const after = parts.slice(1).join('');
        // Rebuild only when the visible total changes; most syncs leave it as is.
        if (node.textContent === parts[0] + formatted + after && node.querySelector('.options-total-amount')) continue;
        const amount = document.createElement('strong');
        amount.className = 'options-total-amount';
        amount.textContent = formatted;
        if (group.motionReady) roll(amount);
        node.replaceChildren(document.createTextNode(parts[0]), amount, document.createTextNode(after));
      }
    }
  }
  sync() {
    for (const branch of this.root.querySelectorAll('[data-options-branch]')) {
      const trigger = branch.parentElement.querySelector('[data-options-trigger]');
      branch.hidden = !trigger?.checked;
    }
    // 2026-11-04 (Owner): add-ons a rule has just made choosable light up one after another.
    const unlocked = [];
    for (const control of this.root.querySelectorAll('input, select, textarea')) {
      const inactive = hiddenByBranch(control) || control.hasAttribute('data-options-blocked');
      if (inactive && activatedControls.has(control)) clearControl(control);
      if (!inactive) activatedControls.add(control);
      if (
        this.motionReady &&
        control.disabled &&
        !inactive &&
        control.matches('[data-options-trigger]') &&
        !control.closest('[data-options-dropdown]')
      )
        unlocked.push(control.closest('.options-swatch'));
      control.disabled = inactive;
    }
    if (unlocked.length) slideIn(unlocked, { distance: 0.5, from: 0.45, duration: 420 });
    for (const choice of this.choices()) {
      const triggers = this.triggers(choice);
      const active = !hiddenByBranch(choice);
      const selected = triggers.filter((control) => control.checked && !control.disabled);
      const validationControl = triggers.find((control) => !control.disabled);
      triggers.forEach((control) => {
        const invalid =
          !choice.dataset.optionsLabel.trim() ||
          !this.root.dataset.optionsTitle.trim() ||
          triggers.some((trigger) => !trigger.value.trim());
        control.setCustomValidity(
          active && control === validationControl
            ? invalid || (choice.dataset.optionsRequired === 'true' && selected.length === 0)
              ? this.text('optionsRequired')
              : ''
            : '',
        );
      });
    }
    let refit = false;
    for (const addon of this.root.querySelectorAll('[data-options-addon]')) {
      const select = addon.querySelector('[data-options-addon-variant]');
      const quantity = addon.querySelector('[data-options-addon-quantity]');
      if (!select || !quantity) continue;
      if (!select.disabled && select.dataset.optionsAutoVariant && !select.value)
        select.value = select.dataset.optionsAutoVariant;
      if (quantity.hasAttribute('data-options-follows-parent'))
        quantity.value = this.form.elements.namedItem('quantity')?.value || '1';
      const option =
        select.selectedOptions[0] ||
        [...select.options].find((item) => item.value === select.dataset.optionsAutoVariant);
      const priceLabel = addon.closest('[data-options-value]')?.querySelector('[data-options-item-price]');
      if (priceLabel && option?.dataset.price) {
        const price = Number(option.dataset.price);
        try {
          const amount =
            price === 0
              ? this.text('optionsFree')
              : option.dataset.priceLabel || currencyFormat(this.root.dataset.optionsCurrency, price);
          const label =
            (price > 0 ? '＋' : '') + amount + (option.dataset.unitPrice ? ` — ${option.dataset.unitPrice}` : '');
          refit = this.showPrice(priceLabel, label, option.dataset.compareLabel || '') || refit;
        } catch {
          /* Liquid's original price remains authoritative when locale data is unavailable. */
        }
      }
      quantity.disabled = select.disabled || !select.value;
      quantity.required = !quantity.disabled;
      quantity.min = option?.dataset.min || '1';
      quantity.step = option?.dataset.step || '1';
      quantity.max = option?.dataset.max || '';
      select.setCustomValidity(!select.disabled && option?.disabled ? this.text('optionsError') : '');
    }
    this.syncVariantPopovers();
    this.syncDropdowns();
    // Only a changed price is fitted again here; typing in the product form does not measure the page.
    if (refit) this.fitPrices();
  }
  collectItems() {
    const errors = [];
    if (this.emptyChoice) errors.push(this.configurationMessage);
    if (this.exclusionError)
      errors.push(this.root.dataset.optionsEditor === 'true' ? this.exclusionError : this.text('optionsUnavailable'));
    if (this.lockedMessage) errors.push(this.lockedMessage);
    if (this.conflictMessage) errors.push(this.conflictMessage);
    if (this.variantAvailable === false) errors.push(this.text('optionsError'));
    for (const input of this.root.querySelectorAll('input, select, textarea')) {
      if (!input.disabled && !input.validity.valid) errors.push(input.validationMessage);
    }
    const items = [];
    let invalidAccessory;
    for (const addon of this.root.querySelectorAll('[data-options-addon]')) {
      const select = addon.querySelector('[data-options-addon-variant]');
      if (!select || select.disabled || !select.value) continue;
      const option = select.selectedOptions[0];
      const item = accessoryItem(
        {
          id: select.value,
          quantity: addon.querySelector('[data-options-addon-quantity]').value,
          available: Boolean(option && !option.disabled),
          minimum: option?.dataset.min,
          increment: option?.dataset.step,
          maximum: option?.dataset.max,
        },
        { _meri_configuration: this.state.identity, _meri_option: this.label(addon) },
      );
      if (item && addon.hasAttribute('data-options-item-addon')) {
        const parentId = this.form.querySelector('[name="id"]')?.value;
        if (!parentId) {
          errors.push(this.text('optionsError'));
          continue;
        }
        if (parentId === item.id) {
          invalidAccessory = addon.closest('[data-options-value]').querySelector('[data-options-trigger]');
          errors.push(this.text('optionsSelfAccessory').replace('__ITEM__', invalidAccessory.value));
          continue;
        }
        item.parent_id = parentId;
        item.properties._meri_follows_parent = '1';
        if (addon.closest('[data-options-choice]')?.dataset.optionsRequired === 'true')
          item.properties._meri_required = '1';
        item.properties[propertyKey(this.propertyTitle || this.root.dataset.optionsTitle)] = addon
          .closest('[data-options-value]')
          .querySelector('[data-options-trigger]').value;
      }
      if (item) items.push(item);
      else errors.push(this.text('optionsError'));
    }
    return { items, errors, invalidAccessory };
  }
  prepare(event) {
    if (event.detail?.form !== this.form) return;
    this.syncAll();
    const { items, errors, invalidAccessory } = this.collectItems();
    this.invalidAccessory = invalidAccessory || null;
    if (errors.length) {
      const invalid = [...this.root.querySelectorAll('input, select, textarea')].find(
        (input) => !input.disabled && !input.validity.valid,
      );
      this.reveal(
        invalid || invalidAccessory || this.root.querySelector('.options-fields'),
        Boolean(invalid || invalidAccessory),
      );
      this.flagProblems();
      event.detail.errors.push(...new Set(errors));
      event.preventDefault();
      if (this.message) this.message.textContent = [...new Set(errors)].join(' ');
      return;
    }
    this.ensureIdentity();
    event.detail.additionalItems.push(...items);
  }
  ensureIdentity() {
    let identity = this.form.querySelector('[data-options-configuration]');
    if (!identity) {
      identity = document.createElement('input');
      identity.type = 'hidden';
      identity.name = 'properties[_meri_configuration]';
      identity.dataset.optionsConfiguration = '';
      this.form.append(identity);
    }
    identity.value = this.state.identity;
  }
  destroy() {
    // A section redraw invalidates the pending reminder, including when sibling groups remain.
    this.state.feedback?.abort();
    this.variantPopovers?.forEach(({ close }) => close?.());
    this.closeHelp(true);
    this.closeName?.(true);
    this.abort.abort();
    this.state.groups.delete(this);
    exclusionStates.delete(this.optionSet || this.root);
    for (const control of this.root.querySelectorAll('input, select, textarea')) control.disabled = true;
    if (!this.state.groups.size) {
      this.form.querySelector('[data-options-configuration]')?.remove();
      this.form.querySelectorAll('[data-options-native-checkout]').forEach((field) => field.remove());
      // Without add-ons Buy it now is the product's own again.
      this.form.querySelector('[data-accelerated-wallet]')?.removeAttribute('inert');
      this.state.shakes?.forEach((animation) => animation.cancel());
      this.state.problems?.forEach((node) => node.removeAttribute('data-options-problem'));
      formStates.delete(this.form);
    } else this.syncAll();
  }
}

if (typeof customElements !== 'undefined' && !customElements.get('meri-options')) {
  customElements.define(
    'meri-options',
    class extends HTMLElement {
      connectedCallback() {
        if (this.controller) return;
        const product = this.closest('[data-meri-product]');
        const details = product?.querySelector('[data-product-details]');
        const buy = details && [...details.children].find((node) => node.dataset.productBlock === 'buy');
        const form = details
          ? buy?.querySelector('[data-meri-product-form]')
          : product?.querySelector('[data-meri-product-form]') || document.getElementById(this.dataset.formId);
        if (form?.matches('[data-meri-product-form]')) this.controller = new OptionsController(this, form);
      }
      disconnectedCallback() {
        this.controller?.destroy();
        this.controller = null;
      }
    },
  );
}
